import {
    useRef,
    useState
} from 'react';

import {Alert, SimpleGrid, Text} from '@mantine/core';
import {modals} from '@mantine/modals';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {useMeters} from '../hooks/useMeters.ts';
import {useMetersSelection} from '../hooks/useMetersSelection.ts';
import {useMetersTableColumns} from '../hooks/useMetersTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {setStatus, update} from '../services/MetersService.ts';
import {buildUpdateMeterInput} from '../services/buildMeterInput.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {Meter} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useMetersSelection();
    const {columns} = useMetersTableColumns();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Meter | null>(null);
    const queryClient = useQueryClient();

    // Editing is a write (and RETIRE de-provisions the Stripe meter), so the row click
    // that opens the edit drawer is gated on UpdateMeter like Add and Delete are on theirs.
    const {allowedActions} = useVerifiedPermissions();
    const canEdit = allowedActions.includes(rbacActions.UpdateMeter);

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useMeters();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    // FNP-768: apply the edit, routing a status change through setMeterStatus (billing-api owns
    // the transition lattice + guards). FIELDS FIRST (updateMeter, via the whitelisted
    // buildUpdateMeterInput — no more blacklist spread), THEN the TRANSITION — so a rejected
    // transition never leaves a de-provisioned meter behind applied edits.
    // Resolves true when saved; the drawer closes only then (FormDrawer).
    const applyEdit = async (record: Meter, values: Meter): Promise<boolean> => {
        try {
            await update(record.id as string, buildUpdateMeterInput(values) as Meter);
            const statusChanged = values.status !== record.status;
            if (statusChanged) {
                await setStatus(record.id as string, values.status as unknown as string);
            }
            queryClient.invalidateQueries({queryKey: ['meters']});
            notifySuccess(I18n.get('Meter updated'));
            return true;
        } catch (e) {
            // Surfaces MeterHasOpenPeriods / IllegalMeterStatusTransition from setMeterStatus.
            notifyError(e);
            return false;
        }
    };

    // FNP-768 (architect caveat): RETIRE de-provisions the live Stripe meter via the FNP-589
    // reconciler — a money-path teardown. A plain Save must NOT silently trigger it, so a
    // transition TO RETIRED is gated behind an explicit confirmation naming the consequence.
    // The submit WAITS for the answer: Cancel (or closing the modal) resolves false, so the
    // drawer stays open with the operator's values instead of discarding them.
    const confirmRetire = (record: Meter, values: Meter) => new Promise<boolean>(resolve => {
        // Mantine fires onConfirm and then onClose; the flag stops onClose from
        // resolving false while the confirmed save is still in flight.
        let confirmed = false;
        modals.openConfirmModal({
            title: I18n.get('Retire this meter?'),
            children: (
                <Text size="sm">
                    {I18n.get('Retiring')} <b>{record.name}</b> ({record.meterCode}) {I18n.get('de-provisions its Stripe billing meter and is terminal. It is blocked if the meter still has open metering periods. Continue?')}
                </Text>
            ),
            labels: {confirm: I18n.get('Retire meter'), cancel: I18n.get('Cancel')},
            confirmProps: {color: 'red'},
            onConfirm: () => {
                confirmed = true;
                applyEdit(record, values).then(resolve);
            },
            onClose: () => {
                if (!confirmed) {
                    resolve(false);
                }
            },
        });
    });

    const handleDrawerSubmit = async (values: Meter): Promise<boolean> => {
        const record = selectedRecord;
        if (!record?.id) {
            return false;
        }

        const retiring = values.status === 'RETIRED' && record.status !== 'RETIRED';
        if (retiring) {
            return confirmRetire(record, values);
        }

        return applyEdit(record, values);
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load meters')}>
                    {I18n.get('The billing API did not return the meters list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<Meter>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={canEdit ? ({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                } : undefined}
            />

            {/*
              TWO EMPTY STATES (§3, §13). "No meters yet" invites the first action; "nothing
              matches" means the search is too narrow. Gated on !loading so it does not
              flash during the first fetch, and on !isError so a failed load keeps its own
              Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching meters') : I18n.get('No meters yet')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded meters matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('A meter defines what usage is measured and how it is aggregated. Use Add Meter to create the first one.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'meter'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
